<template>
  <v-col
    v-if="form"
    cols="12"
  >
    <v-row>
      <v-col cols="6" class="pa-0 pb-2 pr-1">
        <v-select
          v-validate="required.theme"
          :value="currentThemes"
          :items="themes"
          :label="mess['dialog_request_field_themes']"
          :error-messages="errors.collect('themes')"
          item-value="request_theme_id"
          item-text="request_theme_name"
          outlined
          dense
          return-object
          data-vv-name="themes"
          @change="changeThemeHandler($event)"
        />
      </v-col>

      <v-col
        v-if="currentThemes"
        cols="auto"
        class="pa-0 pt-2"
      >
        <v-tooltip
          top
          content-class="request-form-tooltip"
        >
          <template #activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              color="primary"
              dark
            >
              mdi-information-outline
            </v-icon>
          </template>

          <span>{{ currentThemes.request_theme_description }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row
      v-if="currentThemes && technicalProcesses.length"
      key="row-process-group"
      class="flex-wrap"
    >
      <v-col cols="6" class="pa-0 pb-2 pr-1">
        <v-select
          v-validate="required.process"
          :value="currentProcess"
          :items="technicalProcesses"
          :label="mess['dialog_request_field_process']"
          :error-messages="errors.collect('process')"
          item-value="id"
          item-text="name"
          outlined
          dense
          data-vv-name="process"
          return-object
          @change="changeProcessHandler($event)"
        />
      </v-col>

      <v-col
        v-if="currentProcess && currentProcess.product_group"
        cols="6"
        class="pa-0 pb-2 pl-1"
      >
        <v-select
          v-validate="required.group"
          :value="form.group"
          :items="currentProcess.product_group"
          :label="mess['dialog_request_field_product_group']"
          :error-messages="errors.collect('group')"
          item-value="id"
          item-text="name"
          outlined
          dense
          data-vv-name="group"
          return-object
          @change="
            $emit('update:form', {
              key: 'group',
              value: $event
          })"
        />
      </v-col>

      <v-col cols="12" class="pb-8 pt-0">
        <v-divider />
      </v-col>
    </v-row>

    <v-row v-if="isEmpty(company) && parseInt(form.themes.request_theme_id) !== 5666280 && parseInt(form.themes.request_theme_id) !== 4766093">
      <v-col cols="6" class="pa-0 pb-2 pr-1">
        <v-autocomplete
          v-validate="required.company"
          :value="currentCompany"
          :items="companies"
          :label="mess['dialog_request_field_company']"
          :error-messages="errors.collect('company')"
          item-text="title"
          item-value="id"
          outlined
          dense
          data-vv-name="company"
          return-object
          @change="$emit('update:company', $event)"
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { isEmpty } from "lodash";

/**
 * @memberOf RequestDialog
 * @name HeaderForm
 * @desc Компонент шапка формы для отрисовки базовых полей таких как: выбор темы вопроса, выбор компании, выбор процесса и группы.
 *
 * @vue-prop { Object } [company={}] - Текущая компания. Это свойство передается только тогда, когда поп-ап открывается из раздела мои заказчики при клике по названию компании.
 * @vue-prop { Array } [companies=[]] - Массив компаний
 * @vue-prop { Object | Null } form - Форма
 * @vue-prop { Object } [required={}] - Объект с полями обязательными к отправке.
 * @vue-prop { Array } [themes=[]] - Массив объектов, где каждый объект представляет тему обращения.
 *
 * @vue-computed currentProcess - Возвращает текущий выбранный процесс из this.form
 * @vue-computed currentThemes - Возвращает текущую выбранную тему из this.form
 * @vue-computed technicalProcesses - Возвращает технический процесс из this.form.themes
 *
 * @vue-event changeProcessHandler - Обработчик смены процесса. При смене процесса очищаем выбранную группу и передаем
 * выбранный процес в form через $emit. А так же перезапускаем валидатор. В качестве аргумента принимает объект процесса.
 * @vue-event changeThemeHandler - Обработчик смены темы. При смене темы очищаем выбранные группу, процесс и выставляем переданный объект
 * в тему. А так же перезапускаем валидатор.
 * @vue-event resetValidator - Метод перезапуска валидатора.
 */
export default {
  name: "HeaderForm",
  inject: ["$validator"],
  props: {
    company: {
      type: Object,
      default: () => {}
    },
    companies: {
      type: Array,
      default: () => []
    },
    currentCompany: {
      type: Object,
      default: () => {}
    },
    form: {
      type: [Object, null],
      required: true
    },
    required: {
      type: Object,
      default: () => {}
    },
    themes: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    errors() {
      return {};
    },
    currentProcess() {
      return this.form.process;
    },
    currentThemes() {
      return this.form.themes;
    },
    technicalProcesses() {
      return this.form.themes.technical_proceses || [];
    }
  },
  methods: {
    isEmpty,
    changeProcessHandler(item) {
      this.resetValidator();
      this.$emit("update:form", {
        key: "group",
        value: {}
      });
      this.$emit("update:form", {
        key: "process",
        value: item
      });
    },
    changeThemeHandler(item) {
      this.resetValidator();
      this.$emit("update:form", {
        key: "group",
        value: {}
      });
      this.$emit("update:form", {
        key: "process",
        value: {}
      });
      this.$emit("update:form", {
        key: "themes",
        value: item
      });
    },
    resetValidator() {
      this.$validator.pause();
      this.$nextTick(() => {
        this.$validator.errors.clear();
        this.$validator.fields.items.forEach(field => field.reset());
        this.$validator.fields.items.forEach(field =>
          this.errors.remove(field)
        );
        this.$validator.reset();
        this.$validator.resume();
      });
    }
  }
};
</script>
